import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COMPANY_ROUTES } from '../constants';
import { SETTINGS_ROUTES } from '../../shared/settings/constants';
import { useAuth } from '../../shared/auth/hooks/useAuth';
import {
  LayoutDashboard,
  Search,
  History,
  Building2,
  Briefcase,
  Mail,
  Settings,
  LogOut,
  HelpCircle,
  Sparkles,
  Users,
  X,
  Copy,
  ExternalLink,
  MessageCircle,
} from 'lucide-react';
import Sidebar, { SidebarMenuItem } from '../../shared/components/navigation/Sidebar';
import { ConfirmDialog } from '../../shared/components';
import PastChatsList from './PastChatsList';

interface CompanySidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

const CompanySidebar: React.FC<CompanySidebarProps> = ({
  isCollapsed = false,
  onToggle,
  isMobileOpen = false,
  onMobileClose
}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const menuItems: SidebarMenuItem[] = [
    {
      label: 'Dashboard',
      path: COMPANY_ROUTES.DASHBOARD,
      icon: (active) => <LayoutDashboard size={20} strokeWidth={active ? 2.5 : 2} />,
      show: true
    },
    {
      label: 'Talk to David',
      path: '/companies/talk-to-david',
      icon: (active) => <Sparkles size={20} strokeWidth={active ? 2.5 : 2} />,
      show: true
    },
    {
      label: 'Candidate Search',
      path: '/companies/candidate-search',
      icon: (active) => <Search size={20} strokeWidth={active ? 2.5 : 2} />,
      show: false
    },
    {
      label: 'Interested Candidates',
      path: COMPANY_ROUTES.APPLICATIONS_REVIEW,
      icon: (active) => <Users size={20} strokeWidth={active ? 2.5 : 2} />,
      show: true
    },
    {
      label: 'David Inbox',
      path: COMPANY_ROUTES.DAVID_INBOX,
      icon: (active) => <MessageCircle size={20} strokeWidth={active ? 2.5 : 2} />,
      show: true
    },
    {
      // Slice 4.9 — expandable nav item; clicking the row toggles the
      // expansion (no path navigation). The expansion renders PastChatsList
      // which lazy-fetches and shows the recruiter's 10 most recent v2
      // sessions; clicking a row navigates to TalkToDavid?sessionId=<id>.
      label: 'Past chats',
      icon: (active) => <History size={20} strokeWidth={active ? 2.5 : 2} />,
      expandable: true,
      expandedContent: <PastChatsList />,
      show: true
    },
    {
      label: 'Company Profile',
      path: '/companies/profile',
      icon: (active) => <Building2 size={20} strokeWidth={active ? 2.5 : 2} />,
      show: true
    },
    {
      label: 'Job Postings',
      path: COMPANY_ROUTES.PROJECTS,
      icon: (active) => <Briefcase size={20} strokeWidth={active ? 2.5 : 2} />,
      show: true
    },
    {
      label: 'Message',
      path: COMPANY_ROUTES.MESSAGES,
      icon: (active) => <Mail size={20} strokeWidth={active ? 2.5 : 2} />,
      show: false
    },
    {
      label: 'Account Setting',
      path: SETTINGS_ROUTES.ACCOUNT_SETTINGS_COMPANY,
      icon: (active) => <Settings size={20} strokeWidth={active ? 2.5 : 2} />,
      show: true
    }
  ];

  const bottomMenuItems: SidebarMenuItem[] = [
    {
      label: 'Help',
      icon: <HelpCircle size={20} />,
      action: () => setShowHelpModal(true),
      show: true
    }
  ];

  return (
    <>
      <Sidebar
        isCollapsed={isCollapsed}
        onToggle={onToggle || (() => { })}
        isMobileOpen={isMobileOpen}
        onMobileClose={onMobileClose || (() => { })}
        menuItems={menuItems}
        bottomMenuItems={bottomMenuItems}
        onLogout={() => setShowLogoutDialog(true)}
      />

      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowHelpModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-[#0B111E]">Need Help?</h3>
              <button onClick={() => setShowHelpModal(false)} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Reach out to our team and we'll get back to you as soon as possible.</p>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl mb-4">
              <span className="text-sm font-medium text-[#0B111E] flex-1">founders@hushtalent.io</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText('founders@hushtalent.io');
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                title="Copy email"
              >
                <Copy className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            {copied && <p className="text-xs text-green-600 mb-3">Copied to clipboard!</p>}
            <a
              href="mailto:founders@hushtalent.io"
              className="w-full py-3 bg-[#015FA6] text-white font-semibold text-sm rounded-xl hover:bg-[#014a85] transition-colors flex items-center justify-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Send Email
            </a>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showLogoutDialog}
        variant="info"
        title="Log Out?"
        message="You'll be signed out of your Hush account on this device."
        confirmText="Log Out"
        cancelText="Cancel"
        onConfirm={async () => {
          try {
            await logout();
            navigate('/auth/login');
          } catch (error) {
            console.error('Logout failed:', error);
          }
          setShowLogoutDialog(false);
        }}
        onCancel={() => setShowLogoutDialog(false)}
      />
    </>
  );
};

export default CompanySidebar;
